import { useState, useEffect, useRef } from 'react';
import { Package, Download, Loader2, Trash2, Check, X } from 'lucide-react';
import { importJobsAPI } from '../api/importJobs';
import { creatorsAPI } from '../api/creators';

/**
 * Shows recent import jobs as a slide-down panel on the Creators page.
 * Auto-refreshes every 10 s while any job is still running.
 */
export default function ImportsPanel({ isOpen, onClose }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  const fetchJobs = async () => {
    try {
      const response = await importJobsAPI.list({ limit: 20 });
      setJobs(response.data.jobs || []);
    } catch (err) {
      console.error('Failed to load import jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    fetchJobs();

    // Poll every 10 s while any job is incomplete
    intervalRef.current = setInterval(() => {
      const hasRunning = jobs.some((j) => !j.progress?.isComplete);
      if (hasRunning) fetchJobs();
    }, 10000);

    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleExport = async (job) => {
    if (!job.creatorIds || job.creatorIds.length === 0) {
      // Job is still processing — refetch to check if creatorIds populated yet
      try {
        const refreshed = await importJobsAPI.getOne(job.id);
        job = refreshed.data.job;
      } catch {}
      if (!job.creatorIds || job.creatorIds.length === 0) {
        alert('This import is still processing creators. Please wait a moment and try again.');
        return;
      }
    }
    try {
      const response = await creatorsAPI.exportCsv({ ids: job.creatorIds.join(',') });
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `import-${job.name || job.id}.csv`;
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed:', err);
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Remove this import from history? (Creators will not be deleted.)')) return;
    try {
      await importJobsAPI.delete(jobId);
      setJobs((prev) => prev.filter((j) => j.id !== jobId));
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  if (!isOpen) return null;

  const formatTime = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
      ', ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const elapsed = (dt) => {
    const ms = Date.now() - new Date(dt).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hrs = Math.floor(min / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="bg-gray-900/70 border border-white rounded-xl p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-white flex items-center gap-2">
          <Package className="w-4 h-4 text-sc-pink" />
          Import History
        </h2>
        <button
          onClick={onClose}
          className="text-white hover:text-white p-1 rounded-lg hover:bg-white/5"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-white">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      ) : jobs.length === 0 ? (
        <p className="text-xs text-white">No imports yet. Use "Bulk Upload" to start one.</p>
      ) : (
        <div className="space-y-3">
          {jobs.map((job) => {
            const p = job.progress || {};
            const total = p.totalActive ?? job.creatorIds?.length ?? 0;
            const isStillLoading = total === 0 && !p.isComplete;
            const pct = total > 0 ? Math.round((p.enrichedCount / total) * 100) : (isStillLoading ? 5 : 0);
            const isComplete = p.isComplete && !isStillLoading;

            return (
              <div
                key={job.id}
                className="bg-white/3 border border-white/6 rounded-lg p-3"
              >
                {/* Top row: name + time */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-white truncate">
                      {job.name || 'Untitled import'}
                    </h3>
                    <p className="text-[11px] text-white mt-0.5">
                      {formatTime(job.createdAt)} · {total} creator{total !== 1 ? 's' : ''}
                    </p>
                  </div>

                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => handleExport(job)}
                      title="Download enriched CSV"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-sc-pink/15 border border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 transition disabled:opacity-40"
                    >
                      <Download className="w-3 h-3" />
                      CSV
                    </button>
                    <button
                      onClick={() => handleDelete(job.id)}
                      title="Remove from history"
                      className="p-1.5 rounded-lg text-white/20 hover:text-red-400 hover:bg-red-400/10 transition"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mb-2">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      isComplete ? 'bg-emerald-500' : 'bg-sc-pink'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>

                {/* Stats row */}
                <div className="flex items-center gap-3 text-[11px] text-white flex-wrap">
                  {isStillLoading ? (
                    <span className="text-amber-400 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Importing creators…
                    </span>
                  ) : isComplete ? (
                    <span className="text-emerald-400 flex items-center gap-1 font-medium">
                      <Check className="w-3 h-3" /> Complete
                    </span>
                  ) : (
                    <span className="text-sc-pink flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> {p.enrichedCount}/{total} enriched
                    </span>
                  )}
                  {p.emailCount > 0 && (
                    <span>{p.emailCount} email{p.emailCount !== 1 ? 's' : ''}</span>
                  )}
                  {p.linkedUrlsCount > 0 && (
                    <span>{p.linkedUrlsCount} URLs linked</span>
                  )}
                  <span className="ml-auto">{elapsed(job.createdAt)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
